import { Badge, Tooltip, TooltipMessage } from '@carboncopyco/ui';
import { RadioGroup } from '@headlessui/react';
import classNames from 'classnames';
import type React from 'react';
import { useRef } from 'react';
import { EntitySelectCard } from './EntitySelectCard';

export interface Card {
  illustration: React.ReactNode;
  title: string;
  description: string;
  id: string;
  isNew: boolean;
  disabled: boolean;
  disabledMessage?: string;
}

interface EntitySelectedCardRadioGroupProps {
  selected: Card['id'];
  onChange: (value: Card['id']) => void;
  cards: Card[];
}
export const EntitySelectedCardRadioGroup = ({
  selected,
  onChange,
  cards,
}: EntitySelectedCardRadioGroupProps) => {
  const disabledDemoRef = useRef();

  return (
    <RadioGroup
      value={selected}
      onChange={onChange}
    >
      <div className="flex items-center gap-6">
        {cards.map((card: Card) => (
          <RadioGroup.Option
            key={card.id}
            value={card.id}
            disabled={card.disabled}
          >
            {({ checked, disabled }) => {
              const cardProps =
                card.id === 'demo' && disabled ? { ref: disabledDemoRef } : {};
              return (
                <>
                  <div
                    className={classNames(
                      'relative px-16 py-12 rounded-lg border',
                      {
                        'border border-transparent outline outline-2 outline-ds-primary-300 hover:bg-ds-natural-50 hover:cursor-pointer':
                          checked && !disabled,
                        'border border-ds-natural-150 hover:bg-ds-natural-50 hover:cursor-pointer':
                          !checked && !disabled,
                        'bg-ds-natural-200-a hover:cursor-default select-none':
                          disabled,
                      }
                    )}
                    {...cardProps}
                  >
                    {card.isNew && (
                      <div className="max-w-min absolute top-2 -left-2">
                        <Badge
                          label="new"
                          variant="green"
                        />
                      </div>
                    )}
                    <div
                      className={classNames(
                        'w-ds-56 flex justify-center items-center',
                        {
                          'opacity-20': disabled,
                          'opacity-100': !disabled,
                        }
                      )}
                    >
                      <EntitySelectCard
                        illustration={card.illustration}
                        title={card.title}
                        description={card.description}
                      />
                    </div>
                  </div>
                  {disabled && (
                    <Tooltip
                      shadow="md"
                      noArrow
                      placement="top"
                      anchorRef={disabledDemoRef}
                      interactiveBorder={0}
                      fixedWidth
                    >
                      <div className="w-48">
                        <TooltipMessage title="Once you have a demo you can capture a Tour of it" />
                      </div>
                    </Tooltip>
                  )}
                </>
              );
            }}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  );
};
